import 'tui-calendar/dist/tui-calendar.css';
import Calendar from "@toast-ui/react-calendar";
import { useRef, useEffect, MutableRefObject } from 'react';
import dayjs from 'dayjs';
import Select from 'react-select';
import type { DispatchAssignment, DispatchAssignmentEmployee, ReactSelectOption } from '../types/assignment.types';
import { Button, Modal, Form, Spinner } from 'react-bootstrap';

interface CalendarProp extends Calendar {
    calendarInst: any
}

interface scheduleProps {
  id: string
  calendarId: string
  title: string
  start: string
  end: string
  category: string
  isAllDay: boolean
  color: string
  bgColor: string
}

type AssignmentCalendarProps = {
    assignmentsData: DispatchAssignment[]
    navigatePrevious: () => void;
    navigateNext: () => void;
    showAssignmentModal: boolean;
    toggleShowAssignmentModal: (show: boolean) => void;
    updateStartDateField: (date: string) => void;
    updateEndDateField: (date: string) => void;
    updateEmployeeIdField: (id: string) => void;
    startDateField: string | undefined;
    endDateField: string | undefined;
    employeeIdField: Number | undefined;
    employeesData: DispatchAssignmentEmployee[];
    rankField: string | undefined;
    updateRankField: (rank: string) => void;
    createAssignment: () => void;
    searchWeekStart: string | undefined;
    searchServiceAreaId: string | undefined;
    clearStartDateField: () => void;
    clearEndDateField: () => void;
    clearEmployeeIdField: () => void;
    clearRankField: () => void;
    showEditAssignmentModal: boolean;
    toggleShowEditAssignmentModal: (show: boolean) => void;
    startDateEditField: string | undefined;
    endDateEditField: string | undefined;
    rankEditField: string | undefined;
    updateStartDateEditField: (date: string) => void;
    updateEndDateEditField: (date: string) => void;
    updateRankEditField: (rank: string) => void;
    assignmentsRef: MutableRefObject<DispatchAssignment[]>;
    selectedEditAssignment: DispatchAssignment | undefined;
    updateSelectedEditAssignment: (assignment: DispatchAssignment) => void;
    editAssignment: () => void;
    deleteAssignment: () => void;
    showConfirmDeleteModal: boolean;
    toggleShowConfirmDeleteModal: (show: boolean) => void;
    loadingCalendar: boolean;
    toggleLoadingCalendar: (loading: boolean) => void;
}

export default function AssignmentsCalendar(props: AssignmentCalendarProps) {    
    const cal = useRef<CalendarProp>(null);

    const {
        assignmentsData,
        navigatePrevious,
        navigateNext,
        showAssignmentModal,
        toggleShowAssignmentModal,
        updateStartDateField,
        updateEndDateField,
        updateEmployeeIdField,
        startDateField,
        endDateField,
        employeeIdField,
        employeesData,
        rankField,
        updateRankField,
        createAssignment,
        searchWeekStart,
        searchServiceAreaId,
        clearStartDateField,
        clearEndDateField,
        clearEmployeeIdField,
        clearRankField,
        showEditAssignmentModal,
        toggleShowEditAssignmentModal,
        startDateEditField,
        endDateEditField,
        rankEditField,
        updateStartDateEditField,
        updateEndDateEditField,
        updateRankEditField,
        assignmentsRef,        
        selectedEditAssignment,
        updateSelectedEditAssignment,
        editAssignment,
        deleteAssignment,
        showConfirmDeleteModal,
        toggleShowConfirmDeleteModal,
        loadingCalendar,
        toggleLoadingCalendar
    } = props;

    const formatAssignmentsForCalendar = () : scheduleProps[] | [] => {
        if (!assignmentsData || assignmentsData.length === 0) {
            return [];
        }

        return assignmentsData.map((assignment): scheduleProps => ({
            id: assignment.service_area_callout_assignment_id!.toString(),      // TODO: Verify that in this use case, this will never be null
            calendarId: '1',
            title: `${assignment.assignment_rank}: ${assignment.employee_name!}`,       // TODO: Also verify that this will never be null
            start: dayjs(assignment.start_date_local).format('YYYY-MM-DDTHH:mm:ss'),
            end: dayjs(assignment.end_date_local).format('YYYY-MM-DDTHH:mm:ss'),
            isAllDay: true,
            color: 'white',
            bgColor: '#353a40',
            category: 'allday' 
        }))
    }

    const formatSelectedEmployee = (): ReactSelectOption | null => {
        const id = employeeIdField ? Number(employeeIdField) : null;
        if (!id) return null;

        const employee = employeesData.find(e => e.EmployeeID === id);
        if (!employee) return null;

        return { value: employee.EmployeeID.toString(), label: employee.Name };
    };

    const goToNext = async () => {
        toggleLoadingCalendar(true);
        const instance = cal.current?.getInstance();
        if (instance) {
            instance.setDate(dayjs(searchWeekStart).day(0).add(7, 'day').format('YYYY-MM-DD'));
        }
        await navigateNext();
        toggleLoadingCalendar(false);
    }

    const goToPrevious = async () => {
        toggleLoadingCalendar(true);
        const instance = cal.current?.getInstance();
        if (instance) {
            instance.setDate(dayjs(searchWeekStart).day(0).subtract(7, 'day').format('YYYY-MM-DD'));
        }
        await navigatePrevious();
        toggleLoadingCalendar(false);
    }

    const handleCreateAssignmentModalClose = () => {
        clearStartDateField();
        clearEndDateField();
        clearEmployeeIdField();
        clearRankField();
        
        toggleShowAssignmentModal(false);
    };

    const handleEditAssignmentModalClose = () => {
        toggleShowEditAssignmentModal(false);
    }

    const handleScheduleClick = (schedule: any) => {
        const selectedServiceAreaCalloutAssignmentId = !isNaN(Number(schedule.id)) ? Number(schedule.id) : null;
        if (!selectedServiceAreaCalloutAssignmentId) {
            console.log('The schedule ID is invalid.');
            return;
        }

        const assignment = assignmentsRef.current.find(a => a.service_area_callout_assignment_id == Number(schedule.id));

        if (!assignment) {
            console.log('Assignment not found.');
            return;
        }

        updateSelectedEditAssignment(assignment);

        updateStartDateEditField(dayjs(assignment.start_date_local).format('YYYY-MM-DD'));
        updateEndDateEditField(dayjs(assignment.end_date_local).format('YYYY-MM-DD'));
        updateRankEditField(assignment.assignment_rank.toString());
        toggleShowEditAssignmentModal(true);
    }

    const handleDeleteAssignmentClick = () => {
        toggleShowConfirmDeleteModal(true);
    }

    const handleConfirmDeleteModalClose = () => {
        toggleShowConfirmDeleteModal(false);
    }

    useEffect(() => {
        if (searchWeekStart) {
            const instance = cal.current?.getInstance();
            if (instance) {
                instance.setDate(dayjs(searchWeekStart).day(0).format('YYYY-MM-DD'));
            }
        }
    }, [searchWeekStart]);

    return (
        <div className='dispatch-assignments-calendar-wrapper'>
            {loadingCalendar && (
                <div className='dispatch-assignments-calendar-spinner-wrapper'>
                    <Spinner animation="border" variant='primary'/>
                </div>
            )}
            <div className='dispatch-assignments-calendar-nav-button-wrapper'>
                <Button onClick={() => goToPrevious()} disabled={!searchWeekStart}>&#x2190;</Button>
                <Button onClick={() => goToNext()} disabled={!searchWeekStart}>&#x2192;</Button>
                <div className={!searchServiceAreaId ? 'dispatch-assignments-add-button-disabled' : 'dispatch-assignments-add-button'}
                onClick={() => !searchServiceAreaId ? null : toggleShowAssignmentModal(true)}>
                    <span className="material-icons">assignment_add</span>
                </div>
            </div>
            <Calendar
                isReadOnly={false}
                ref={cal}
                height={"600px"}
                view={'week'}
                schedules={formatAssignmentsForCalendar()}
                taskView={false}
                scheduleView={['allday']}
                useDetailPopup={false}
                onClickSchedule={(event: any) => {
                    const schedule = event.schedule;
                    handleScheduleClick(schedule);
                }}
            />
            <Modal size='lg' show={showAssignmentModal} onHide={() => handleCreateAssignmentModalClose()} backdrop='static' centered>
                <Modal.Header closeButton>
                    <Modal.Title>Create Call Out Assignment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        <div>Start Date</div>
                        <Form.Control
                            type='date'
                            name='startDate'
                            value={startDateField}
                            onChange={(e) => updateStartDateField(e.target.value)}
                        />
                    </div>
                    <div>
                        <div>End Date</div>
                        <Form.Control
                            type='date'
                            name='endDate'
                            value={endDateField}
                            onChange={(e) => updateEndDateField(e.target.value)}
                        />
                    </div>
                    <div>
                        <div>Employee</div>
                        <Select options={
                            employeesData.map((employee) => ({
                                value: employee.EmployeeID, label: employee.Name }))
                            }
                            value={formatSelectedEmployee()}
                            onChange={(selected: ReactSelectOption) => {
                                updateEmployeeIdField(selected.value);
                            }}
                        />
                    </div>
                    <div>
                        <div>Assignment Rank</div>
                        <Form.Control
                            type='text'
                            placeholder='Assignment Rank'
                            value={rankField}
                            onChange={(e) => updateRankField(e.target.value)}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => createAssignment()}>Save</Button>
                    <Button variant='secondary' onClick={() => handleCreateAssignmentModalClose()}>Cancel</Button>
                </Modal.Footer>
            </Modal>
            <Modal size='lg' show={showEditAssignmentModal} onHide={() => handleEditAssignmentModalClose()} backdrop='static' centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Call Out Assignment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        <div style={{fontWeight:'bold'}}>{selectedEditAssignment?.employee_name}</div>
                    </div>
                    <br />
                    <div>
                        <div>Start Date</div>
                        <Form.Control
                            type='date'
                            name='startDateEdit'
                            value={startDateEditField}
                            onChange={(e) => updateStartDateEditField(e.target.value)}
                        />
                    </div>
                    <br />
                    <div>
                        <div>End Date</div>
                        <Form.Control
                            type='date'
                            name='endDateEdit'
                            value={endDateEditField}
                            onChange={(e) => updateEndDateEditField(e.target.value)}
                        />
                    </div>
                    <br />                   
                    <div>
                        <div>Assignment Rank</div>
                        <Form.Control
                            type='text'
                            placeholder='Assignment Rank'
                            value={rankEditField}
                            onChange={(e) => updateRankEditField(e.target.value)}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='danger' onClick={() => handleDeleteAssignmentClick()}>Delete</Button>
                    <Button variant='primary' onClick={() => editAssignment()}>Save</Button>
                    <Button variant='secondary' onClick={() => handleEditAssignmentModalClose()}>Cancel</Button>
                </Modal.Footer>
            </Modal>
            <Modal size='sm' show={showConfirmDeleteModal} onHide={() => handleConfirmDeleteModalClose()} backdrop='static' centered>
                <Modal.Header>
                    <Modal.Title>Confirm delete?</Modal.Title>
                </Modal.Header>
                <Modal.Footer>
                    <Button variant='danger' onClick={() => deleteAssignment()}>Delete</Button>
                    <Button variant='secondary' onClick={() => handleConfirmDeleteModalClose()}>Cancel</Button>                    
                </Modal.Footer>
            </Modal>
        </div>
    )
}